import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  Image,
  Trash2,
  FolderPlus,
  ChevronLeft,
  Folder,
  Edit2,
  AlertCircle,
  CheckCircle,
  Loader,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { useSearchParams } from "react-router-dom";
import { folderEvents } from "../lib/events";
import MetadataModal from "./MetadataModal";
import SEO from "./SEO";
import Compressor from "compressorjs";

interface Asset {
  id: string;
  name: string;
  url: string;
  size: number;
  created_at: string;
  folder?: string;
  alt_description?: string;
  metadata?: any;
}

interface FolderInfo {
  name: string;
  count: number;
}

export default function AdminAssets() {
  const [searchParams] = useSearchParams();
  const folderParam = searchParams.get("folder");

  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentFolder, setCurrentFolder] = useState<string>(folderParam || "");
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [draggedAsset, setDraggedAsset] = useState<Asset | null>(null);

  useEffect(() => {
    setCurrentFolder(folderParam || "");
  }, [folderParam]);

  useEffect(() => {
    fetchAssets();
    fetchFolders();
  }, [currentFolder]);

  const fetchFolders = async () => {
    try {
      // Get all assets from database to count by folder
      const { data: dbAssets, error: dbError } = await supabase
        .from("assets")
        .select("folder");

      if (dbError) throw dbError;

      // Count assets by folder
      const folderCounts = (dbAssets || []).reduce(
        (acc: Record<string, number>, asset) => {
          if (asset.folder != null) {
            const folderName = String(asset.folder);
            acc[folderName] = (acc[folderName] || 0) + 1;
          }
          return acc;
        },
        {},
      );

      // List all folders in storage
      const { data: storageData, error: storageError } = await supabase.storage
        .from("assets")
        .list("", {
          limit: 1000,
          sortBy: { column: "name", order: "asc" },
        });

      if (storageError) throw storageError;

      // Create folder list with counts
      const folderList = (storageData || [])
        .filter((item) => !String(item.name || "").includes("."))
        .map((item) => ({
          name: String(item.name || ""),
          count: folderCounts[String(item.name || "")] || 0,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setFolders(folderList);
    } catch (error) {
      console.error("Error fetching folders:", error);
      setError(
        error instanceof Error ? error.message : "Failed to load folders",
      );
      setFolders([]);
    }
  };

  const fetchAssets = async () => {
    try {
      setLoading(true);

      // Fetch assets from database
      const { data, error } = await supabase
        .from("assets")
        .select("*")
        .eq("deleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Filter by current folder
      const filteredAssets = (data || []).filter((asset) => {
        if (currentFolder) {
          return String(asset.folder || "") === currentFolder;
        }
        return !asset.folder;
      });

      setAssets(filteredAssets);
    } catch (err) {
      console.error("Error fetching assets:", err);
      setError(err instanceof Error ? err.message : "Failed to load assets");
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      setError("Folder name cannot be empty");
      return;
    }

    const sanitizedFolderName = newFolderName
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-");

    try {
      setError(null);

      // Create an empty file as folder marker
      const emptyBlob = new Blob([new Uint8Array([0x00])], {
        type: "application/octet-stream",
      });
      const { error } = await supabase.storage
        .from("assets")
        .upload(`${sanitizedFolderName}/.folder`, emptyBlob);

      if (error) throw error;

      setShowNewFolderInput(false);
      setNewFolderName("");
      await fetchFolders();
      folderEvents.emit();
      setSuccessMessage("Folder created successfully");
    } catch (err) {
      console.error("Error creating folder:", err);
      setError("Failed to create folder");
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      try {
        for (const file of acceptedFiles) {
          // Check file size
          if (file.size > 10 * 1024 * 1024) {
            // 10MB
            throw new Error(`File ${file.name} exceeds maximum size of 10MB`);
          }

          // Check file type
          if (!file.type.startsWith("image/")) {
            throw new Error(`File ${file.name} is not an image`);
          }

          // Compress image
          const compressedFile = await new Promise<File>((resolve, reject) => {
            new Compressor(file, {
              quality: 0.8,
              maxWidth: 1920,
              maxHeight: 1080,
              success(result) {
                const compressedFile = new File([result], file.name, {
                  type: result.type,
                });
                resolve(compressedFile);
              },
              error(err) {
                reject(err);
              },
            });
          });

          // Generate a unique filename
          const fileExt = file.name.split(".").pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
          const filePath = currentFolder
            ? `${currentFolder}/${fileName}`
            : fileName;

          // Upload to storage
          const { error: uploadError } = await supabase.storage
            .from("assets")
            .upload(filePath, compressedFile, {
              cacheControl: "3600",
              upsert: false,
            });

          if (uploadError) throw uploadError;

          // Get public URL
          const {
            data: { publicUrl },
          } = supabase.storage.from("assets").getPublicUrl(filePath);

          // Insert into database
          const { error: dbError } = await supabase.from("assets").insert([
            {
              name: file.name,
              url: publicUrl,
              size: compressedFile.size,
              folder: currentFolder || null,
            },
          ]);

          if (dbError) throw dbError;
        }

        await fetchAssets();
        await fetchFolders();
        setSuccessMessage("Assets uploaded successfully");
      } catch (err) {
        console.error("Error uploading assets:", err);
        setError(
          err instanceof Error ? err.message : "Failed to upload assets",
        );
      } finally {
        setUploading(false);
      }
    },
    [currentFolder],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const handleDelete = async (asset: Asset) => {
    if (
      !confirm(
        "Are you sure you want to delete this asset? This action cannot be undone.",
      )
    )
      return;

    try {
      // Delete from storage
      const filePath = asset.url.split("/assets/")[1];
      if (!filePath) throw new Error("Invalid file path");

      const { error: storageError } = await supabase.storage
        .from("assets")
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("assets")
        .update({ deleted: true })
        .eq("id", asset.id);

      if (dbError) throw dbError;

      // Refresh data
      await fetchAssets();
      await fetchFolders();
      setSuccessMessage("Asset deleted successfully");
    } catch (err) {
      console.error("Error deleting asset:", err);
      setError("Failed to delete asset");
    }
  };

  const handleDeleteFolder = async (folderName: string) => {
    if (
      !confirm(
        `Are you sure you want to delete the folder "${folderName}" and all its contents? This action cannot be undone.`,
      )
    )
      return;

    try {
      // List all files in the folder
      const { data: files, error: listError } = await supabase.storage
        .from("assets")
        .list(folderName);

      if (listError) throw listError;

      // Delete all files in storage
      if (files && files.length > 0) {
        const filePaths = files.map((file) => `${folderName}/${file.name}`);
        const { error: deleteError } = await supabase.storage
          .from("assets")
          .remove(filePaths);

        if (deleteError) throw deleteError;
      }

      // Delete folder marker
      await supabase.storage.from("assets").remove([`${folderName}/.folder`]);

      // Delete all assets in this folder from database
      const { error: dbError } = await supabase
        .from("assets")
        .update({ deleted: true })
        .eq("folder", folderName);

      if (dbError) throw dbError;

      // Refresh data
      await fetchAssets();
      await fetchFolders();
      setSuccessMessage("Folder deleted successfully");
    } catch (err) {
      console.error("Error deleting folder:", err);
      setError("Failed to delete folder");
    }
  };

  const handleMetadataSave = async (asset: Asset, metadata: any) => {
    try {
      const { error } = await supabase
        .from("assets")
        .update({
          alt_description: metadata.alt_description,
          metadata: {
            caption: metadata.caption,
            credit: metadata.credit,
            license: metadata.license,
            tags: metadata.tags,
          },
        })
        .eq("id", asset.id);

      if (error) throw error;

      setEditingAsset(null);
      await fetchAssets();
      setSuccessMessage("Asset metadata updated successfully");
    } catch (err) {
      console.error("Error updating asset metadata:", err);
      setError("Failed to update asset metadata");
    }
  };

  const handleDragStart = (asset: Asset) => {
    setDraggedAsset(asset);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetFolder: string) => {
    e.preventDefault();
    if (!draggedAsset) return;

    // Don't move if the asset is already in the target folder
    if (draggedAsset.folder === targetFolder) return;

    try {
      setError(null);

      // Get the old file path
      const oldPath = draggedAsset.url.split("/assets/")[1];
      if (!oldPath) throw new Error("Invalid file path");

      // Create the new file path
      const fileName = oldPath.split("/").pop();
      if (!fileName) throw new Error("Invalid file name");
      const newPath = targetFolder ? `${targetFolder}/${fileName}` : fileName;

      // Copy the file to the new location
      const { error: copyError } = await supabase.storage
        .from("assets")
        .copy(oldPath, newPath);

      if (copyError) throw copyError;

      // Delete the old file
      const { error: deleteError } = await supabase.storage
        .from("assets")
        .remove([oldPath]);

      if (deleteError) throw deleteError;

      // Get the new public URL
      const {
        data: { publicUrl },
      } = supabase.storage.from("assets").getPublicUrl(newPath);

      // Update the asset record in the database
      const { error: updateError } = await supabase
        .from("assets")
        .update({
          url: publicUrl,
          folder: targetFolder || null,
        })
        .eq("id", draggedAsset.id);

      if (updateError) throw updateError;

      await fetchAssets();
      await fetchFolders();
      setSuccessMessage("Asset moved successfully");
    } catch (err) {
      console.error("Error moving asset:", err);
      setError("Failed to move asset");
    } finally {
      setDraggedAsset(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-12 w-12 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="One80Labs - Manage Assets"
        description="Admin interface for managing image assets in the One80Labs content management system."
        noindex={true}
      />
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-900">Assets</h1>
          {currentFolder && (
            <button
              onClick={() => setCurrentFolder("")}
              className="inline-flex items-center text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              Back to root
            </button>
          )}
        </div>
        <button
          onClick={() => setShowNewFolderInput(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <FolderPlus className="h-5 w-5 mr-2" />
          New Folder
        </button>
      </div>

      {showNewFolderInput && (
        <div className="mb-6 flex items-center space-x-4">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Enter folder name"
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
          />
          <button
            onClick={createFolder}
            className="px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c]"
          >
            Create
          </button>
          <button
            onClick={() => {
              setShowNewFolderInput(false);
              setNewFolderName("");
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {!currentFolder && folders.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Folders</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {folders.map((folder) => (
              <div
                key={folder.name}
                className={`bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between ${
                  draggedAsset ? "cursor-copy" : ""
                }`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, folder.name)}
              >
                <button
                  onClick={() => setCurrentFolder(folder.name)}
                  className="flex items-center text-gray-900 hover:text-[#F98B3D]"
                >
                  <Folder className="h-5 w-5 mr-2" />
                  <span className="flex-1">{folder.name}</span>
                  <span className="ml-2 text-sm text-gray-500">
                    ({folder.count})
                  </span>
                </button>
                <button
                  onClick={() => handleDeleteFolder(folder.name)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete folder"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        {...getRootProps()}
        className={`mb-8 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? "border-[#F98B3D] bg-orange-50"
            : "border-gray-300 hover:border-[#F98B3D]"
        }`}
      >
        <input {...getInputProps()} />
        <Image className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-600">
          {isDragActive
            ? "Drop the images here..."
            : `Drag & drop images here, or click to select files${currentFolder ? ` (uploading to /${currentFolder})` : ""}`}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Supported formats: JPG, PNG, GIF, WebP, SVG (max 10MB)
        </p>
      </div>

      {uploading && (
        <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <Loader className="h-5 w-5 text-blue-400 animate-spin" />
            <div className="ml-3">
              <p className="text-sm text-blue-700">Uploading images...</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {assets.map((asset) => (
          <div
            key={asset.id}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-move"
            draggable
            onDragStart={() => handleDragStart(asset)}
          >
            <div className="aspect-square relative group">
              <img
                src={asset.url}
                alt={asset.alt_description || asset.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditingAsset(asset)}
                    className="p-2 text-white hover:text-[#F98B3D] transition-colors"
                    title="Edit metadata"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(asset)}
                    className="p-2 text-white hover:text-red-500 transition-colors"
                    title="Delete asset"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-medium text-gray-900 truncate">
                {asset.name}
              </h3>
              <p className="text-sm text-gray-500">
                {formatFileSize(asset.size)}
              </p>
              {asset.alt_description && (
                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                  {asset.alt_description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {assets.length === 0 && !loading && (
        <div
          className={`text-center py-12 ${draggedAsset ? "border-2 border-dashed border-gray-300 rounded-lg" : ""}`}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, "")}
        >
          <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {currentFolder
              ? `No assets found in folder /${currentFolder}`
              : "No assets found. Upload some images to get started."}
          </p>
          {draggedAsset && !currentFolder && (
            <p className="text-sm text-gray-500 mt-2">
              Drop here to move to root folder
            </p>
          )}
        </div>
      )}

      {editingAsset && (
        <MetadataModal
          asset={editingAsset}
          onClose={() => setEditingAsset(null)}
          onSave={handleMetadataSave}
        />
      )}
    </div>
  );
}
